/**
 * Auth Context — Thin wrapper around Clerk hooks
 *
 * Exposes the same interface downstream components expect:
 * email, isAuthenticated, isLoading, login, logout, checkSession.
 *
 * Adds getToken() for Bearer auth in api.ts.
 */

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/react'
import { useNavigate } from 'react-router-dom'
import { setTokenProvider } from '@/services/api'

type AuthState = {
  email: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

type AuthContextType = AuthState & {
  login: () => void
  logout: () => Promise<void>
  checkSession: () => Promise<void>
  getToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded: userLoaded } = useUser()
  const { isSignedIn, getToken } = useClerkAuth()
  const clerk = useClerk()
  const navigate = useNavigate()

  const email = user?.primaryEmailAddress?.emailAddress ?? null
  const isAuthenticated = !!isSignedIn
  const isLoading = !userLoaded

  const login = useCallback(() => {
    navigate('/login')
  }, [navigate])

  const logout = useCallback(async () => {
    await clerk.signOut()
    navigate('/login')
  }, [clerk, navigate])

  // No-op for backwards compat — Clerk manages session automatically
  const checkSession = useCallback(async () => {
    // Clerk handles session refresh internally
  }, [])

  const fetchToken = useCallback(async (): Promise<string | null> => {
    return getToken()
  }, [getToken])

  // Wire the token provider into the API client
  useEffect(() => {
    setTokenProvider(fetchToken)
  }, [fetchToken])

  return (
    <AuthContext.Provider
      value={{
        email,
        isAuthenticated,
        isLoading,
        login,
        logout,
        checkSession,
        getToken: fetchToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
